import TrimbleMaps, { Map, LngLatBounds, Route, Marker, Popup, FullscreenControl, NavigationControl, FitBoundsOptions, LngLatLike, PopupOptions, LngLat, MapOptions, RouteOptions, MarkerOptions } from "@trimblemaps/trimblemaps-js";
import { GeoService } from '../GeoService'
import { IONICWrapper } from "../../utils/common";
import { KEYS, ROUTE_COLOR, EXPAND_MAP, COLLAPSE_MAP, EXPAND_MAP_SRC, COLLAPSE_MAP_SRC, PCmiler } from '../../utils/constants'
import HTTP from '../httpServices';
TrimbleMaps.APIKey = KEYS.PCMILER_API_KEY;

// should contain raw set of features of PCmiler

class PCMilerMapClass {
    private _MapInstance: any = TrimbleMaps;

    private PCmilerRegion: string = IONICWrapper.details.keys 
            && IONICWrapper.details.keys.configuration.PCmilerRegion 
                || PCmiler.supportedRegion;

    private PCmilerCountries: string = IONICWrapper.details.keys 
            && IONICWrapper.details.keys.configuration.PCmilerCountries 
                || PCmiler.supportedCountries;

    /**
     * Default Map Location set to North America
    */
    protected readonly _MapDefaultRegion: string = TrimbleMaps.Common.Region.NA
    /**
     * Default Route Color
    */
    private readonly _routeColor: string = ROUTE_COLOR
    /**
     * Default Show Stops
    */
    private readonly _showStops: boolean = false
    // false -> Custom Marker, True -> Default Marker
    /**
     * createMap will create our Map Instance
    */
    protected createMapInstance(container: string | HTMLElement, bounds: LngLatLike | LngLatBounds, style: string, fitBoundsOptions?: FitBoundsOptions): Map {
        return new this._MapInstance.Map({
            container: container,
            region: this._MapDefaultRegion,
            trackResize: true,
            bounds,
            fitBoundsOptions: fitBoundsOptions || {},
            boxZoom: true,
            style,
            failIfMajorPerformanceCaveat: false,
        } as MapOptions);
    }
    /**
     * createLngLatBounds will create our Map Bounds
    */
    protected createLngLatBounds(sw: LngLatLike, ne: LngLatLike): LngLatBounds {
        return new this._MapInstance.LngLatBounds(sw, ne);
    }
    /**
     * createRoute will create our Map Route
    */
    protected createRouteInstance(routeId: string, stops: Array<LngLatLike>, frameOptions: TrimbleMaps.FitBoundsOptions): Route {
        return new this._MapInstance.Route({ routeId, stops, routeColor: this._routeColor, showStops: this._showStops, frameOptions } as RouteOptions);
    }
    /**
     * createMarker will create our custom Marker
    */
    protected createMarker(svgContent: HTMLElement, lng: number, lat: number): Marker {
        return new this._MapInstance.Marker({ element: svgContent } as MarkerOptions).setLngLat([lng, lat]);
    }
    /**
     * createPopUp will create our custom Popup
    */
    protected createPopup(options: PopupOptions): Popup {
        return new this._MapInstance.Popup(options);
    }
    /**
     * createMarker will create our custom Marker
    */
    protected createFullscreenControl(): FullscreenControl {
        return new this._MapInstance.FullscreenControl();
    }
    /**
     * getGeocode will get the Latitude and Longitude details for the stops
    */
    protected getGeocode() {
        return this._MapInstance.Geocoder;
    }
    /**
     * getLngLat will fetch Longitude and Latitude
    */
    protected getLngLat(lng: number, lat: number): LngLat {
        return new this._MapInstance.LngLat(lng, lat);
    }
    /**
     * navigationControl will add Zoom In & Zoom Out control
    */
    protected navigationControl(options: GeoService.NavigationControlOptions): NavigationControl {
        return new this._MapInstance.NavigationControl(options);
    }
    /**
     * ExpandCollapseControl
     */
    protected expandCollapseControl(isDialog: boolean, onExpand: Function, onCollapse: Function): ExpandCollapseControl {
        return new ExpandCollapseControl(isDialog, onExpand, onCollapse);
    }
    /**
     * Search current Address based on geo coordinates (lat, long).
     * * @param {string} query string for PCmiler Maps search.
     *@param {*} getAddress callback for PCmiler Maps address search.
     * @returns null.
    */
    protected async searchCurrentAddressPCmiler(query: string, getAddress: GeoService.GetAddressFunction) {
        new HTTP().__requestRestApi(`https://singlesearch.alk.com/${this.PCmilerRegion}/api/search?authToken=${
            KEYS.PCMILER_API_KEY
            }&countries=${this.PCmilerCountries}&query=${query}`, {}, 'get').then((response: any) => {
                getAddress(response.data.Locations as GeoService.SinglesearchLocation[]);
            }).catch(async (error: any) => { });
    }

    /**
     * Search list of address based on search address query params.
     * * @param {string} query string for PCmiler Maps search.
     *@param {*} getAddress callback for PCmiler Maps address search.
     * @returns null.
    */
    protected async searchAddressPCmiler(query: string, getAddress: GeoService.GetAddressFunction) {
        
        new HTTP().__requestRestApi(`https://singlesearch.alk.com/${this.PCmilerRegion}/api/search?authToken=${
            KEYS.PCMILER_API_KEY
            }&countries=${this.PCmilerCountries}&query=${query}`, {}, 'get').then((response: any) => {
                getAddress(response.data.Locations as GeoService.SinglesearchLocation[]);
            }).catch(async (error: any) => { });
    }

    /**
     * destroy Map instance
    */
    protected onDestroy() {
        this._MapInstance = null
    }
}
export class ExpandCollapseControl {
    private isDialog: boolean;
    private map: any;
    private onExpand: Function;
    private onCollapse: Function;
    expandCtrl: HTMLButtonElement;
    collapseCtrl: HTMLButtonElement;
    constructor(isDialog: boolean, onExpand: Function, onCollapse: Function) {
        this.isDialog = isDialog;
        this.onExpand = onExpand;
        this.onCollapse = onCollapse;
        this.expandCtrl = document.createElement("button");
        this.collapseCtrl = document.createElement("button");
    }
    onAdd(map: any) {
        this.map = map;
        this.expandCtrl.innerHTML = EXPAND_MAP;
        this.expandCtrl.addEventListener("click", (e: Event) => {
            e.preventDefault();
            e.stopPropagation();
            this.onExpand();
        });
        this.expandCtrl.className = "expand-map";
        this.expandCtrl.style.cursor = "pointer";
        this.expandCtrl.style.pointerEvents = "auto";

        const expandImg: HTMLImageElement = document.createElement("img");
        expandImg.className = "expand-img";
        expandImg.src = EXPAND_MAP_SRC;
        this.expandCtrl.insertBefore(expandImg, this.expandCtrl.firstChild);

        this.collapseCtrl.innerHTML = COLLAPSE_MAP;
        this.collapseCtrl.addEventListener("click", () => {
            this.onCollapse();
        });
        this.collapseCtrl.className = "collapse-map";
        this.collapseCtrl.style.pointerEvents = "auto";

        const collapseImg: HTMLImageElement = document.createElement("img");
        collapseImg.className = "collapse-img";
        collapseImg.src = COLLAPSE_MAP_SRC;

        this.collapseCtrl.insertBefore(collapseImg, this.collapseCtrl.firstChild);
        return this.isDialog ? this.collapseCtrl : this.expandCtrl
    }
    onRemove() {
        this.expandCtrl.parentNode && this.expandCtrl.parentNode.removeChild(this.expandCtrl);
        this.collapseCtrl.parentNode && this.collapseCtrl.parentNode.removeChild(this.collapseCtrl);
        this.map = undefined;
    }
}

export {
    PCMilerMapClass
}
